import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { StageSetup, StageSetupParams } from '../../interfaces/career-setup/stage-setup.interface';
import { ApiResponse } from '../../interfaces/ApiResponse';
import { PageResponse } from '../../interfaces/page-response.interface';
import { environment } from '../../../../environments/environment.dev';
import { convertToHttpParams, CommonSearchParam } from '../../interfaces/common-search-param.interface';
import { StageSetupService } from './stage-setup.service';

@Injectable({
  providedIn: 'root'
})
export class StageSetupApiService extends StageSetupService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}v1/setup-data/stage-setup`;

  constructor() {
    super();
  }

  getStages(params: StageSetupParams): Observable<StageSetup[]> {
    const httpParams = convertToHttpParams(params);
    return this.wrapRequest(
      this.http.get<ApiResponse<StageSetup[]>>(this.baseUrl, {
        headers: this.headers,
        params: httpParams
      }).pipe(
        map(res => res?.response || [])
      ),
      false,
      false
    );
  }

  override findAll(params: CommonSearchParam): Observable<PageResponse<StageSetup>> {
    const httpParams = convertToHttpParams(params);
    return this.wrapRequest(
      this.http.get<ApiResponse<PageResponse<StageSetup>>>(this.baseUrl, {
        headers: this.headers,
        params: httpParams
      }).pipe(
        map(res => res?.response || { content: [], page: { number: 0, size: 0, totalElements: 0, totalPages: 0 } })
      ),
      false,
      false
    );
  }

  override find(id: string): Observable<StageSetup> {
    return this.wrapRequest(
      this.http.get<ApiResponse<StageSetup>>(`${this.baseUrl}/${id}`, { headers: this.headers }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override create(data: Partial<StageSetup>): Observable<StageSetup> {
    return this.wrapRequest(
      this.http.post<ApiResponse<StageSetup>>(this.baseUrl, data, { headers: this.headers }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override update(id: string, data: Partial<StageSetup>): Observable<StageSetup> {
    return this.wrapRequest(
      this.http.put<ApiResponse<StageSetup>>(`${this.baseUrl}/${id}`, data, { headers: this.headers }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override updateStatus(id: string, isActive: boolean): Observable<StageSetup> {
    return this.wrapRequest(
      this.http.patch<ApiResponse<StageSetup>>(`${this.baseUrl}/${id}/status`, null, {
        headers: this.headers,
        params: { isActive: isActive.toString() }
      }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override delete(id: string): Observable<void> {
    return this.wrapRequest(
      this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`, { headers: this.headers }).pipe(
        map(() => undefined)
      ),
      false,
      false
    );
  }
}
